import {
    ApplicationRef,
    createComponent,
    ComponentRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
    Type,
    EnvironmentInjector,
    inject,
    DOCUMENT
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { ApModalComponent } from './modal.component';
import { ModalClosedReason, ModalContent, ModalOptions } from './modal.interface';

@Injectable({
    providedIn: 'root'
})
export class ApModalService {
    private injector = inject(Injector);
    private appRef = inject(ApplicationRef);
    private readonly document = inject(DOCUMENT);
    private environmentInjector = inject(EnvironmentInjector);

    /**
     * amount of modal changes
     * @internal
     */
    public modalCountChanged: Observable<number>;

    private _openModals = 0;
    private readonly modalStack: Array<ApModalComponent> = [];
    private readonly modalCountChangedSubject: Subject<number>;
    public constructor() {
        this.modalCountChangedSubject = new Subject<number>();
        this.modalCountChanged = this.modalCountChangedSubject.asObservable();
    }

    public get openModals(): number {
        return this._openModals;
    }

    public open<ComponentT extends ModalContent<OptionsT>, OptionsT>(
        component: Type<ComponentT>,
        options: ModalOptions<OptionsT> = new ModalOptions<any>()
    ): ApModalComponent {
        const componentRef = createComponent(ApModalComponent, {
            elementInjector: this.injector,
            environmentInjector: this.environmentInjector
        });
        this.appRef.attachView(componentRef.hostView);
        const htmlFromComponentRef = this.getHtmlElementFromComponentRef(componentRef);
        const dialogElement = htmlFromComponentRef.childNodes[0] as HTMLDialogElement;
        this.document.body.appendChild(htmlFromComponentRef);

        componentRef.instance.setModalContent(component, options);
        componentRef.instance.setModalFooter(options);

        /**
         * Set timeout so that content is rendered first in the dom and then opened
         * This will focus the first focusable element correctly.
         * Otherwise the first focusable element will not be in the DOM yet when opened, and will therefore not focus.
         * This can be removed when dialog is not dynamically injected but is already present in the DOM.
         */
        requestAnimationFrame(() => {
            dialogElement.showModal();
        });

        componentRef.instance
            .afterClosed()
            .pipe(
                finalize(() => {
                    dialogElement.close();
                    this.appRef.detachView(componentRef.hostView);
                    componentRef.destroy();
                    --this._openModals;
                    this.modalCountChangedSubject.next(this._openModals);
                    this.modalStack.pop();
                })
            )
            .subscribe();

        this.modalStack.push(componentRef.instance);
        ++this._openModals;
        this.modalCountChangedSubject.next(this._openModals);
        return componentRef.instance;
    }

    public openSubModal<ComponentT extends ModalContent<OptionsT>, OptionsT>(
        closeParentCallback: () => void,
        component: Type<ComponentT>,
        options: ModalOptions<OptionsT> = new ModalOptions<any>()
    ): ApModalComponent {
        const componentRef = createComponent(ApModalComponent, {
            elementInjector: this.injector,
            environmentInjector: this.environmentInjector
        });
        this.appRef.attachView(componentRef.hostView);
        const htmlFromComponentRef = this.getHtmlElementFromComponentRef(componentRef);
        const dialogElement = htmlFromComponentRef.childNodes[0] as HTMLDialogElement;
        this.document.body.appendChild(htmlFromComponentRef);

        componentRef.instance.setModalContent(component, options);

        /**
         * Set timeout so that content is rendered first in the dom and then opened
         * This will focus the first focusable element correctly.
         * Otherwise the first focusable element will not be in the DOM yet when opened, and will therefore not focus.
         * This can be removed when dialog is not dynamically injected but is already present in the DOM.
         */
        requestAnimationFrame(() => {
            dialogElement.showModal();
        });

        componentRef.instance
            .afterClosed()
            .pipe(
                finalize(() => {
                    dialogElement.close();
                    this.appRef.detachView(componentRef.hostView);
                    componentRef.destroy();
                    --this._openModals;
                    this.modalCountChangedSubject.next(this._openModals);

                    // show the parent modal;
                    const modalToShow = this.modalStack[this.modalStack.length - 2] as any;
                    if (modalToShow?.elementRef) {
                        modalToShow.elementRef.nativeElement.hidden = false;
                    }

                    this.modalStack.pop();
                }),
                tap((result) => {
                    if (result.reason === ModalClosedReason.CANCELLED) {
                        closeParentCallback();
                    }
                })
            )
            .subscribe();

        this.modalStack.push(componentRef.instance);

        // hide the parent modal;
        const modalToHide = this.modalStack[this.modalStack.length - 2] as any;
        if (modalToHide?.elementRef) {
            modalToHide.elementRef.nativeElement.hidden = true;
        }
        ++this._openModals;
        this.modalCountChangedSubject.next(this._openModals);
        return componentRef.instance;
    }

    /**
     * closes the current modal
     */
    public closeCurrentModal(): void {
        if (this.modalStack.length > 0) {
            this.modalStack.pop().cancel();
        }
    }

    /**
     * get title of the current modal
     * @returns string
     */
    public getCurrentModalTitle(): string {
        return this.modalStack.slice(-1)[0]?.modalOptions.title ?? '';
    }

    /**
     * modal is cancelable or not
     * @returns boolean
     */
    public getCurrentModalIsCancelable(): boolean {
        return this.modalStack.slice(-1)[0]?.modalOptions.cancelable;
    }

    /**
     * gets the HTML element for a ComponentRef by finding the first rootNode in the hostView and casting it to an HTMLElement
     */
    private getHtmlElementFromComponentRef(componentRef: ComponentRef<ApModalComponent>): HTMLElement {
        return (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    }
}
